import { Component, inject } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ILinkButton } from '../../../dashboards/interfaces/tiles/definition-helpers/link-button.interface';
import { IBannerTileDefinition } from '../../../dashboards/interfaces/tiles/definitions/banner-tile-definition.interface';
import { TileCreatorService } from '../../services/tile-creator.service';
import { TILE_REQUEST } from '../../tokens/tile-request.token';
import { TileSetupWizardComponent } from '../tile-setup-wizard.component';

@Component({
  standalone: false,
  selector: 'app-banner-setup-wizard',
  templateUrl: './banner-setup-wizard.component.html',
  styleUrls: ['./banner-setup-wizard.component.scss'],
})
export class BannerSetupWizardComponent extends TileSetupWizardComponent<IBannerTileDefinition> {
  firstStepSetupForm: FormGroup;
  secondStepSetupForm: FormGroup;

  dataSourceObject;

  get headingControl(): AbstractControl {
    return this.firstStepSetupForm.get('heading') as AbstractControl;
  }

  get displayFeatureTotalControl(): AbstractControl {
    return this.firstStepSetupForm.get('displayFeatureTotal') as AbstractControl;
  }

  get subHeadingControl(): AbstractControl {
    return this.firstStepSetupForm.get('subHeading') as AbstractControl;
  }

  get headingIconControl(): AbstractControl {
    return this.firstStepSetupForm.get('headingIcon') as AbstractControl;
  }

  get imgUriControl(): AbstractControl {
    return this.firstStepSetupForm.get('imgUri') as AbstractControl;
  }

  get linkButtonsControl(): FormArray {
    return this.secondStepSetupForm.get('linkButtons') as FormArray;
  }

  constructor(
    private readonly fb: FormBuilder,
    private readonly tileCreatorService: TileCreatorService,
  ) {
    super();

    this.setupForm.addControl('firstStep', this.firstStepSetupForm);
    this.setupForm.addControl('secondStep', this.secondStepSetupForm);

    this.tileInstance = inject(TILE_REQUEST).tileInstance;
  }

  ngOnInit() {
    this.initSetupForms();

    this.hookForm();
  }

  initSetupForms() {
    this.firstStepSetupForm = this.fb.group({
      heading: ['', [Validators.required]],
      subHeading: ['', [Validators.required]],
      displayFeatureTotal: ['', [Validators.required]],
      headingIcon: ['', [Validators.required]],
      imgUri: ['', [Validators.required]],
    });

    this.secondStepSetupForm = this.fb.group({
      linkButtons: this.fb.array([]),
    });

    if (this.tileInstance) {
      this.firstStepSetupForm.patchValue(this.tileInstance);
      this.secondStepSetupForm.patchValue(this.tileInstance);

      this.tileInstance.linkButtons?.forEach((linkButton: ILinkButton) => {
        this.addLinkButton(linkButton);
      });
    }
  }

  addLinkButton(linkButton?: ILinkButton) {
    const linkButtonFormGroup = this.fb.group({
      buttonText: ['', [Validators.required]],
      buttonIcon: ['', [Validators.required]],
      buttonColorTheme: ['', [Validators.required]],
      routeLink: ['', [Validators.required]],
    });

    linkButtonFormGroup.patchValue(linkButton);

    this.linkButtonsControl.push(linkButtonFormGroup);
  }

  removeLinkButton(linkButtonIndex: number) {
    this.linkButtonsControl.removeAt(linkButtonIndex);
  }

  initDataSource(dataSource: any) {
    this.dataSourceObject = dataSource;
  }

  onCreate() {
    Object.assign(this.tileInstance, this.getChanges());
    this.tileCreatorService.onChange.next();
  }

  getChanges() {
    return {
      tileType: 'banner',
      heading: this.headingControl.value,
      headingIcon: this.headingIconControl.value,
      displayFeatureTotal: this.displayFeatureTotalControl.value,
      subHeading: this.subHeadingControl.value,
      imgUri: this.imgUriControl.value,
      customHeight: '25vh',
      linkButtons: this.linkButtonsControl.value,
      canView: 'true',
    };
  }
}
